import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '../config';
import { InAppNotification } from '../types/inAppNotification.types';

/**
 * Hook to fetch in-app notifications for the current user
 * Listens to changes in users/{userId}/notifications collection
 * Can optionally filter by hotelId to show only notifications for specific hotel
 * Can optionally filter by number of days (e.g., last 3 days, last 7 days)
 * Can optionally filter to show only unread notifications
 */
export const useInAppNotifications = (
  userId: string | undefined,
  hotelId?: string | undefined,
  daysLimit?: number,
  maxNotifications = 50,
  onlyUnread = false,
) => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const notificationsRef = collection(db, 'users', userId, 'notifications');

      // Calculate date threshold if daysLimit is provided
      let dateThreshold: Timestamp | undefined;
      if (daysLimit) {
        const thresholdDate = new Date();
        thresholdDate.setDate(thresholdDate.getDate() - daysLimit);
        dateThreshold = Timestamp.fromDate(thresholdDate);
      }

      // Build query - filter by hotelId, date, and/or unread status
      let q = query(notificationsRef, orderBy('createdAt', 'desc'), limit(maxNotifications));

      if (onlyUnread && hotelId && dateThreshold) {
        q = query(
          notificationsRef,
          where('isRead', '==', false),
          where('hotelId', '==', hotelId),
          where('createdAt', '>=', dateThreshold),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (onlyUnread && hotelId) {
        q = query(
          notificationsRef,
          where('isRead', '==', false),
          where('hotelId', '==', hotelId),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (onlyUnread && dateThreshold) {
        q = query(
          notificationsRef,
          where('isRead', '==', false),
          where('createdAt', '>=', dateThreshold),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (onlyUnread) {
        q = query(
          notificationsRef,
          where('isRead', '==', false),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (hotelId && dateThreshold) {
        q = query(
          notificationsRef,
          where('hotelId', '==', hotelId),
          where('createdAt', '>=', dateThreshold),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (hotelId) {
        q = query(
          notificationsRef,
          where('hotelId', '==', hotelId),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      } else if (dateThreshold) {
        q = query(
          notificationsRef,
          where('createdAt', '>=', dateThreshold),
          orderBy('createdAt', 'desc'),
          limit(maxNotifications),
        );
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetchedNotifications: InAppNotification[] = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          } as InAppNotification));

          setNotifications(fetchedNotifications);

          const unread = fetchedNotifications.filter((notification) => !notification.isRead).length;
          setUnreadCount(unread);
          setHasMore(fetchedNotifications.length >= maxNotifications);
          setError(null);
          setLoading(false);
        },
        (err) => {
          setError(err as Error);
          setLoading(false);
        },
      );

      return () => {
        unsubscribe();
      };
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return undefined;
    }
  }, [userId, hotelId, maxNotifications, daysLimit, onlyUnread]);

  return { notifications, loading, error, unreadCount, hasMore };
};
